import * as Sentry from '@sentry/react';
import { openModal } from 'features/modal/modalSlice';
import { store } from 'store/store';
import { formatErrorForLogging, logger } from 'utils/logger';
import { ControlledReceivingHandle } from 'utils/webrtc/ControlledReceivingHandle';
import { RTCClient } from 'utils/webrtc/index';
import { ReceivingFeed } from 'utils/webrtc/ReceivingFeed';
import { JanusConnection, ReceivingNegotiationMeta } from 'utils/webrtc/types';
import { IceLogger } from 'utils/webrtc/IceLogger';
import { JanusJS } from 'lib/janus';
import { eventBus } from 'utils/eventBus';
import { selectIceRelay, selectIceRelayFallback } from 'features/application/applicationSlice';
import { IceRecoveryLadder } from 'utils/webrtc/IceRecoveryLadder';
import { prepareIceRestart } from 'utils/webrtc/prepareIceRestart';
import { DtlsStallRecovery } from 'utils/webrtc/DtlsStallRecovery';
import { isTurnCredentialRefused } from 'utils/webrtc/turnCredentials';
import { RejectedJoinCode } from 'utils/webrtc/subscribeGrantError';

export class BaseReceiver {
  connection?: JanusConnection;

  feed: ReceivingFeed;

  plugin?: ControlledReceivingHandle;

  constructor(receivingFeed: ReceivingFeed) {
    this.feed = receivingFeed;
  }

  negotiationMeta: ReceivingNegotiationMeta = {
    iceRestartAttempt: false,
    mediaFailureReported: false,
  };

  iceRecovery = new IceRecoveryLadder();

  // Set by subclasses that can rebuild a never-established PC. Absent → the modal.
  rebuildHandler?: (feed: ControlledReceivingHandle) => Promise<boolean>;

  dtlsRecovery = new DtlsStallRecovery({
    feedType: 'receiving',
    canRecover: () =>
      !!this.plugin &&
      !RTCClient.supressErrors &&
      this.feed === RTCClient.receivingFeed &&
      !this.negotiationMeta.mediaFailureReported,
    canRebuild: () => this.rebuildHandler !== undefined,
    connectionStateSupported: () =>
      this.plugin?.janusPlugin?.webrtcStuff?.pc?.connectionState !== undefined,
    rebuild: () => this.rebuildHandler!(this.plugin!),
    cancelIceRestart: () => this.clearIceRestartTimer(),
    reportFailure: () =>
      this.reportMediaFailure('connection_failed', 'failed', { previouslyConnected: true }),
    onRebuildThrew: () => this.feed.cleanupFeed(),
  });

  iceRestartHandler: (feed: ControlledReceivingHandle) => Promise<void> = async () => {};

  retrySubscription: (
    connectionHandle: string,
    transactionId: string,
    errorMessage: string
  ) => void = () => {};

  recoverFromRejectedJoin: (
    feed: ControlledReceivingHandle,
    code: RejectedJoinCode
  ) => Promise<void> = async () => {};

  onIceState = (state: RTCIceConnectionState, stats?: JanusJS.IceConnectionStats) => {
    if (!this.plugin?.handle) {
      return;
    }

    // Superseded by Client.reset(): the PC is orphaned and would falsely classify
    // as network_blocked, so drop the event before any log, Sentry or modal fires.
    if (this.feed !== RTCClient.receivingFeed) {
      return;
    }

    const peerConnection = this.plugin.janusPlugin?.webrtcStuff?.pc;

    if ((state === 'connected' || state === 'completed') && stats) {
      IceLogger.logConnectionSuccess(this.plugin.handle, 'receiving', stats, peerConnection);
    } else if (state === 'failed' || state === 'disconnected') {
      IceLogger.logConnectionFailure(this.plugin.handle, 'receiving', state, stats);
    } else {
      IceLogger.logConnectionState(this.plugin.handle, 'receiving', state);
    }

    const restore = (reason: 'disconnected' | 'failed') => {
      if (RTCClient.supressErrors) {
        return;
      }

      if (this.negotiationMeta.mediaFailureReported) {
        return;
      }

      // A pending restart already owns this death; `disconnected` → `failed`
      // for one outage must not walk the ladder twice.
      if (this.negotiationMeta.iceRestartTimer !== undefined) {
        return;
      }

      // ICE up, DTLS never completed: the ladder's restart cannot help.
      if (this.dtlsRecovery.onIceDisconnected()) {
        return;
      }

      const decision = this.iceRecovery.decide(reason, stats?.previouslyConnected ?? false);

      if (decision.action === 'report') {
        this.reportMediaFailure(decision.cause, state, stats, decision.connectedLifetimeMs);
        return;
      }

      this.negotiationMeta.iceRestartTimer = window.setTimeout(async () => {
        this.negotiationMeta.iceRestartTimer = undefined;

        const feed = this.plugin;
        if (!feed || this.negotiationMeta.mediaFailureReported) {
          return;
        }

        logger
          .remote({ system: true, capture: 'streaming' })
          .warn(`Subscribing peer connection ${reason}. Trying to recover...`, {
            handleId: feed.handle,
            relay: decision.relay,
            // prepareIceRestart skips relay silently when the flag is off, so without
            // this a disabled run is indistinguishable from a rung that declined relay.
            relayFallbackEnabled: selectIceRelayFallback(store.getState()),
            connectedLifetimeMs: decision.connectedLifetimeMs,
            stableReset: decision.stableReset,
            ...this.iceRecovery.snapshot(),
          });

        prepareIceRestart({
          pc: feed.janusPlugin?.webrtcStuff?.pc,
          ladder: this.iceRecovery,
          decision,
          handleId: feed.handle,
          feedType: 'receiving',
          reason,
        });

        this.negotiationMeta.iceRestartAttempt = true;

        try {
          await this.iceRestartHandler(feed);
        } catch (error) {
          if (RTCClient.supressErrors) {
            return;
          }

          const message = 'ICE restart request failed';
          const context = {
            handleId: feed.handle,
            direction: 'subscribing',
            reason,
          };

          logger.remote({ system: true, capture: 'streaming' }).error(message, context);

          Sentry.captureException(new Error(message, { cause: error }), {
            tags: { component: 'BaseReceiver', operation: 'ice-restart' },
            contexts: { iceRestart: { ...context, ...formatErrorForLogging(error) } },
          });
        }
      }, 5000);
    };

    if (state === 'connected' || state === 'completed') {
      this.dtlsRecovery.onIceConnected();

      if (this.negotiationMeta.iceRestartTimer !== undefined) {
        this.iceRecovery.forgiveUndispatchedDeath();
      }

      this.clearIceRestartTimer();
      this.negotiationMeta.iceRestartAttempt = false;

      if (this.iceRecovery.hasConnected) {
        this.feed.recheckQualityAfterReconnect();
      }

      this.iceRecovery.markConnected();

      // Engines pass through 'connected' before 'completed'; log the relay once.
      if (state === 'connected' && this.iceRecovery.isRelayActive) {
        IceLogger.logRelayConnected(this.plugin.handle, 'receiving', peerConnection);
      }
    }

    if (state === 'disconnected' || state === 'failed') {
      restore(state);
    }
  };

  onConnectionState = (state: RTCPeerConnectionState) => {
    if (!this.plugin?.handle || this.feed !== RTCClient.receivingFeed) {
      return;
    }

    this.dtlsRecovery.onConnectionState(state);
  };

  onWebRTCConnectionUp = () => {
    this.dtlsRecovery.onWebRTCUp();
  };

  // Subclasses call this at the top of `attachPlugin`: every handle gets a fresh PC.
  protected onNewPeerConnection = () => {
    this.dtlsRecovery.onNewPeerConnection();
  };

  // Subclasses call this from `oncleanup`: janus nulls the PC before it fires, so a pending
  // restart would configure a dead handle and a pending rebuild has nothing to wait for.
  protected onPeerConnectionGone = () => {
    this.clearIceRestartTimer();
    this.dtlsRecovery.onPeerConnectionGone();
    this.dtlsRecovery.abandonRebuild(RTCClient.supressErrors ? 'teardown' : 'pc_gone');
  };

  // Janus nulls the PC before `oncleanup` fires, so a pending restart would configure a dead handle.
  protected clearIceRestartTimer = () => {
    window.clearTimeout(this.negotiationMeta.iceRestartTimer);
    this.negotiationMeta.iceRestartTimer = undefined;
  };

  onIceGatheringState = (state: RTCIceGatheringState, candidateStats?: any) => {
    if (!this.plugin?.handle) {
      return;
    }

    if (state !== 'complete') {
      return;
    }

    const peerConnection = this.plugin.janusPlugin?.webrtcStuff?.pc;
    const iceServers = peerConnection?.getConfiguration()?.iceServers;
    IceLogger.logGatheringComplete(
      this.plugin.handle,
      'receiving',
      candidateStats,
      iceServers,
      peerConnection
    );

    // Chrome enumerates host/mDNS candidates with no network access, so exactly 0
    // proves the PC is wedged in 'new' with no death for onIceState to react to.
    if (candidateStats?.gathered !== 0) {
      return;
    }

    if (RTCClient.supressErrors || this.feed !== RTCClient.receivingFeed) {
      return;
    }

    // A renegotiation can re-gather nothing (Firefox bundling a new m-line), and a live PC keeps
    // its join-time credential: neither the empty gathering nor that expiry means it failed.
    const iceState = peerConnection?.iceConnectionState;
    if (iceState === 'connected' || iceState === 'completed') {
      return;
    }

    const { hasConnected } = this.iceRecovery;
    // An empty restart is the ladder's to retry only on the PC that connected (a fresh PC parks in
    // 'new'); a refused/expired TURN credential ends recovery anyway, as Rejoin gets a fresh one.
    if (
      this.dtlsRecovery.iceEverConnected &&
      !isTurnCredentialRefused(candidateStats.candidateErrors, iceServers)
    ) {
      return;
    }

    this.reportMediaFailure(
      hasConnected ? 'connection_failed' : 'network_blocked',
      iceState ?? 'new',
      {
        gathered: 0,
        nonHostGathered: 0,
        previouslyConnected: hasConnected,
        candidateErrors: candidateStats.candidateErrors,
      }
    );
  };

  // Callers must gate on `RTCClient.supressErrors`; the helper only dedupes
  // via `mediaFailureReported`.
  reportMediaFailure = (
    cause: 'connection_failed' | 'network_blocked',
    state: RTCIceConnectionState,
    stats?: Partial<JanusJS.IceConnectionStats>,
    connectedLifetimeMs: number | null = null
  ) => {
    if (this.negotiationMeta.mediaFailureReported) {
      return;
    }

    const { plugin } = this;
    if (!plugin) {
      return;
    }

    const direction = 'subscribing' as const;
    const pluginType = plugin.janusPlugin.getPlugin();
    const handleId = plugin.handle;
    const iceRelay = selectIceRelay(store.getState());
    const message = 'Subscribing peer connection failed';
    const iceFallback = { ...this.iceRecovery.snapshot(), connectedLifetimeMs };
    const turnCredentialRefused = isTurnCredentialRefused(
      stats?.candidateErrors,
      plugin.janusPlugin?.webrtcStuff?.pc?.getConfiguration()?.iceServers
    );
    // A refused or expired TURN credential is not a network block: Rejoin fetches a fresh one.
    const reportedCause =
      cause === 'network_blocked' && turnCredentialRefused ? 'connection_failed' : cause;

    const candidatesGathered = stats?.gathered ?? 0;
    const nonHostCandidatesGathered = stats?.nonHostGathered ?? 0;
    const previouslyConnected = stats?.previouslyConnected ?? false;

    logger.remote({ capture: 'streaming' }).error(message, {
      handleId,
      pluginType,
      direction,
      iceConnectionState: state,
      candidatesGathered,
      nonHostCandidatesGathered,
      previouslyConnected,
      iceCompleted: stats?.iceCompleted ?? false,
      durationMs: stats?.duration,
      iceRelay,
      iceFallback,
      cause: reportedCause,
      turnCredentialRefused,
    });

    Sentry.captureException(new Error(message), {
      contexts: {
        mediaFailure: {
          cause: reportedCause,
          turnCredentialRefused,
          direction,
          pluginType,
          iceRelay,
          iceConnectionState: state,
          candidatesGathered,
          nonHostCandidatesGathered,
          previouslyConnected,
          iceCompleted: stats?.iceCompleted ?? false,
          durationMs: stats?.duration,
          handleId,
        },
        iceFallback,
      },
    });

    this.surfaceMediaFailure(reportedCause);
  };

  // `mediaFailureReported` is also what disarms the ICE restart and the DTLS rebuild.
  protected surfaceMediaFailure = (cause: 'connection_failed' | 'network_blocked') => {
    const direction = 'subscribing' as const;

    eventBus.sendMessage('mediaConnectionFailed', {}, { direction, cause });
    store.dispatch(openModal('mediaConnectionFailed', { cause, direction }));

    this.negotiationMeta.mediaFailureReported = true;
  };

  // Janus tears the PC down before Chrome re-emits a terminal `iceConnectionState`, and its
  // `reason` is the only server-side verdict on a PC that never completed DTLS.
  onWebRTCConnectionDown = (rawReason?: string) => {
    if (RTCClient.supressErrors) {
      return;
    }

    // Superseded by Client.reset(): drop the event so this orphan can't
    // open the modal when Janus tears down its handle.
    if (this.feed !== RTCClient.receivingFeed) {
      return;
    }

    // After the teardown guards on purpose: leave/reset hang up every handle.
    IceLogger.logPeerConnectionDown(this.plugin?.handle, 'receiving', {
      reason: rawReason,
      iceRestartAttempt: this.negotiationMeta.iceRestartAttempt,
      mediaFailureReported: this.negotiationMeta.mediaFailureReported,
    });

    this.dtlsRecovery.routeHangup(rawReason, this.negotiationMeta);
  };
}
